import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAPI } from '../../API';
import { ActionTypes, useAppDispatch, useAppState } from '../../Store';
import { useTranslation } from '../../Translation';
import SidebarNav from '../../Components/SidebarNav/SidebarNav';
import useAuthenticated from '../Dashboard/useAuthenticated';
import Table from '../../Components/Table/Table';
import LoadingCircleTransparent from '../../Components/LoadingIndicators/LoadingCircleTransparent';
import { usePaginations } from '../../Hooks/usePaginations';
import { useDateFormat } from '../../Hooks/useDateFormat';
import Modal, { useModal } from '../../Components/Modal';
import { Button } from '../../Components/Buttons';
import { TextInput } from '../../Components/Inputs/TextInput';
import { useRefresh } from '../../Hooks/useRefresh';
import { PencilIcon, TrashIcon } from '@heroicons/react/20/solid';
import ClearAll from '../../Resources/Images/Files/ClearAll';
import Select from '../../Components/Inputs/Select';
import { badgeColorsChoices } from '../../Components/Badge';
import { timezoneOptions } from '../../Components/AutomatisationSteps/Delay/DelayPanel';

export const useScheduledTaskMappingForTable = (scheduledTasks, automatisations) => {
    const { translate } = useTranslation();
    const { formatDate } = useDateFormat();

    return scheduledTasks.map((scheduledTask) => {
        // Create a new object without the data and is_deleted properties
        const { data, is_deleted, ...scheduledTaskWithoutUnwantedProps } = scheduledTask;
        return {
            ...scheduledTaskWithoutUnwantedProps,
            key: scheduledTask.id,
            created_at: formatDate(scheduledTask.created_at),
            automatisation_name: automatisations.find(automatisation => automatisation.id === scheduledTask.automatisation_id)?.name
        };
    });
}

export default function ScheduledTask() {

    const { translate, language, setLanguage } = useTranslation();

    const { isAuthenticated, pathAfterLogin } = useAuthenticated();

    const api = useAPI();

    const [automatisations, setAutomatisations] = useState([]);
    const [scheduledTasks, setScheduledTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const pagination = usePaginations();
    const { open, setOpen } = useModal();
    const { refreshId, refresh } = useRefresh();
    const { page, pageSize } = pagination;
    const [selectedScheduledTask, setSelectedScheduledTask] = useState(null);
    const navigate = useNavigate();


    useEffect(() => {
        const fetchContacts = async () => {
            try {
                setLoading(true);
                const response = await api.listScheduledTasks(page, pageSize, pagination.sortBy, pagination.sortOrder);
                const automatisationsResponse = await api.listPaginatedCampaigns(1, 999999);
                setScheduledTasks(response.data.scheduled_tasks);
                setAutomatisations(automatisationsResponse.data.automatisations);
                pagination.setMetaData(response.data.meta);
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        }

        fetchContacts();
    }, [pagination.page, pagination.sortBy, pagination.sortOrder, refreshId]);

    const handleSort = ({ key }) => {
        if (pagination.sortBy === key) {
            pagination.setSortOrder(pagination.sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            pagination.setSortBy(key);
            pagination.setSortOrder('asc');
        }
        pagination.setPage(1);
    }


    const handleEdit = (scheduledTask) => {
        try {
            setOpen(true);
        } catch (error) {
            console.error(error);
        }
    }

    const handleDelete = async (scheduledTask) => {
        try {
            await api.deleteScheduledTask(scheduledTask.id);
            refresh();
        } catch (error) {
            console.error(error);
        }
    }

    const mappedScheduledTasks = useScheduledTaskMappingForTable(scheduledTasks, automatisations);

    return (
        <>
            {/* Place the Modal outside the main container */}
            <Modal
                open={open}
                onClose={() => setOpen(false)}
                title={selectedScheduledTask ? translate('scheduled_tasks.edit') : translate('scheduled_tasks.create')}
                description={''}
                children={<ScheduledTaskModal refresh={refresh} setOpen={setOpen} scheduledTask={selectedScheduledTask} setSelectedScheduledTask={setSelectedScheduledTask} open={open} campaigns={automatisations} />}
            />

            <div className="flex min-h-full w-full">
                <SidebarNav currentPage="scheduledTask">
                    {loading ? <div className="flex justify-center items-center h-full w-12">
                        <LoadingCircleTransparent />
                    </div> : (
                        <Table
                            title={translate('scheduled_tasks.title')}
                            description={translate('scheduled_tasks.description')}
                            data={mappedScheduledTasks}
                            translationKey="scheduled_tasks.table"
                            columns={['name', 'automatisation_name', 'delay_between_runs', 'timezone', 'hour_to_run']}
                            pagination={pagination}
                            onSort={handleSort}
                            createButton={{
                                text: translate('scheduled_tasks.create'),
                                action: () => setOpen(true)
                            }}
                            onRowClick={(row) => {}}
                            dropdownGroups={[{
                                options: [{
                                    label: translate('edit'),
                                    icon: <PencilIcon aria-hidden="true" className="mr-3 size-5 text-gray-400 group-data-[focus]:text-gray-500" />,
                                    action: (row) => { setSelectedScheduledTask(row); setOpen(true) }
                                }
                                ]
                            }, {
                                options: [{
                                    label: translate('delete'),
                                    icon: <TrashIcon aria-hidden="true" className="mr-3 size-5 text-red-400 group-data-[focus]:text-gray-500" />,
                                    action: (row) => handleDelete(row)
                                }]
                            }]}
                        />
                    )}
                </SidebarNav>
            </div>
        </>
    );
}

export const ScheduledTaskModal = ({ refresh, setOpen, scheduledTask, setSelectedScheduledTask, open, campaigns }) => {

    const [name, setName] = useState(scheduledTask ? scheduledTask.name : '');
    const [delayBetweenRuns, setDelayBetweenRuns] = useState(scheduledTask ? scheduledTask.delay_between_runs : '');
    const [automatisationId, setAutomatisationId] = useState(scheduledTask ? scheduledTask.automatisation_id : '');
    const [timezone, setTimezone] = useState(scheduledTask ? scheduledTask.timezone : undefined);
    const [hourToRun, setHourToRun] = useState(scheduledTask ? scheduledTask.hour_to_run : undefined);
    const { translate } = useTranslation();
    const api = useAPI();

    const selectedCampaign = campaigns.find(campaign => campaign.id === automatisationId);

    const handleCreate = async () => {
        try {
            await api.createScheduledTask(name, delayBetweenRuns, automatisationId, timezone, hourToRun);
            refresh();

            setOpen(false);
            setName('');
        } catch (error) {
            console.error(error);
        }
    }

    const handleUpdate = async () => {
        try {
            await api.updateScheduledTask(scheduledTask.id, name, delayBetweenRuns, automatisationId, timezone, hourToRun);
            refresh();
            setOpen(false);
        } catch (error) {
            console.error(error);
        }
    }

    useEffect(() => {
        if (!open) {
            setName('');
            setDelayBetweenRuns(undefined);
            setAutomatisationId(undefined);
            setTimezone(undefined);
            setHourToRun(undefined);
            setSelectedScheduledTask(null);
        }
    }, [open]);

    const selectedTimezone = timezoneOptions.find((tzOption) => tzOption.value.toLowerCase() === timezone?.toLowerCase());

    return (
        <div className="mt-4 overflow-visible">
            <TextInput id="name" name="name" label={translate('scheduled_tasks.name')} placeholder={translate('scheduled_tasks.name_placeholder')} value={name} onChange={(e) => setName(e.target.value)} />
            <div className="flex flex-col mt-4">
                <TextInput type="number" id="delay_between_runs" name="delay_between_runs" label={translate('scheduled_tasks.delay_between_runs')} placeholder={translate('scheduled_tasks.delay_between_runs_placeholder')} value={delayBetweenRuns} onChange={(e) => setDelayBetweenRuns(e.target.value)} />
            </div>
            <div className="flex flex-col mt-4">
                <Select title={translate('scheduled_tasks.automatisation_id')} options={campaigns} selected={selectedCampaign} setSelected={(campaign) => setAutomatisationId(campaign.id)} />
            </div>
            <div className="flex flex-col mt-4">
                <Select title={translate('scheduled_tasks.timezone')} options={timezoneOptions} selected={selectedTimezone} setSelected={(timezone) => setTimezone(timezone.value)} />
            </div>
            <div className="flex flex-col mt-4">
                <TextInput type="number" id="hour_to_run" name="hour_to_run" label={translate('scheduled_tasks.hour_to_run')} placeholder={translate('scheduled_tasks.hour_to_run_placeholder')} value={hourToRun} onChange={(e) => setHourToRun(e.target.value)} />
            </div>
            <div className="flex justify-end mt-4">
                <Button disabled={!name || name.length === 0} onClick={scheduledTask ? handleUpdate : handleCreate}>{scheduledTask ? translate('update') : translate('create')}</Button>
            </div>
        </div>
    )
}